import React, { useState, useRef, useEffect, useCallback } from "react";
import BrandMark from "./BrandMark";
import { Search, ChevronDown, LogOut, User as UserIcon, ArrowLeftRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// LT-26 — single Libraries dropdown replaces the per-library top-nav links.
// Order matches the user's explicit list (locked in LT-26 spec).
const LIBRARY_ITEMS = [
  { label: "Master Library",    href: "/library",             match: /^\/library/ },
  { label: "Labour Rates",      href: "/labour-rates",        match: /^\/labour-rates/ },
  { label: "Material Rates",    href: "/materials",           match: /^\/materials/ },
  { label: "Prelim Library",    href: "/prelim-library",      match: /^\/prelim-library/ },
  { label: "Pro Sum Library",   href: "/provsum-library",     match: /^\/provsum-library/ },
  { label: "A&O Library",       href: "/ao-library",          match: /^\/ao-library/ },
  { label: "Notes Library",     href: "/notes-library",       match: /^\/notes-library/ },
  // LT-21b — Job Templates (phase structure + default library lines per job
  // type) live under Libraries.
  { label: "Job Templates",     href: "/templates",           match: /^\/templates/ },
  // LT-38 — request-for-quote email wording lives with the other libraries.
  { label: "Email Templates",   href: "/email-templates",     match: /^\/email-templates/ },
  // LT-17 §6 — Labour Rates and Material Rates rejoin the Libraries
  // menu. Both are priced reusable data consumed by every tender AND
  // every recipe (Master Library → Edit → labour picker + material
  // picker both read from them), which is exactly what this menu is
  // for. Previously they were only reachable via the Settings shortcut
  // card, which contradicted the "lives under Libraries →" copy on
  // the card itself.
  //
  // "Omissions Library" removed from the Libraries menu — the same
  // data is authored under A&O Library. Two entry points for the
  // same collection was reading as duplicated navigation. The
  // /exclusions-library route still resolves for any bookmarks.
];

// LT-26 — top nav: Dashboard | Tenders | Libraries ▾ | Settings.
// Removed (per spec): standalone library links, Materials, Labour, Templates, Documents, Reports.
const NAV_ITEMS = [
  { label: "Dashboard", href: "/dashboard" },
  { label: "Tenders",   href: "/tenders",   match: /^\/tenders/ },
  { label: "Insights",  href: "/insights",  match: /^\/insights/ },
  { label: "Settings",  href: "/settings",  match: /^\/settings/ },
];

function NavLink({ label, active, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      className={[
        "px-3.5 py-1.5 rounded-md transition-colors",
        active ? "bg-accent-soft text-accent font-medium" : "text-ink-2 hover:bg-surface-2 hover:text-ink"
      ].join(" ")}
      style={{ fontSize: 13 }}
    >
      {label}
    </button>
  );
}

function LibrariesDropdown() {
  const loc = useLocation();
  const nav = useNavigate();
  // LT-Nav-Click — two independent open signals that OR together.
  //   hoverOpen  = transient peek from mouse pointer / trigger focus
  //   stickyOpen = explicit user intent from a click; resists hover-close,
  //                only clears on second click, Escape, click-outside, or
  //                focus-out.
  // Previously the click and hover states were coupled through a single
  // boolean, which meant hover would set open=true just before a click,
  // so the click toggle looked like it did nothing.
  const [hoverOpen, setHoverOpen] = useState(false);
  const [stickyOpen, setStickyOpen] = useState(false);
  const open = hoverOpen || stickyOpen;
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const active = LIBRARY_ITEMS.some((it) => it.match.test(loc.pathname));

  const close = useCallback(() => {
    setHoverOpen(false);
    setStickyOpen(false);
  }, []);

  // Click-outside → close (only matters while sticky-open; hover-only
  // already closes on mouse-leave).
  useEffect(() => {
    if (!stickyOpen) return;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [stickyOpen, close]);

  const onTriggerClick = () => {
    if (stickyOpen) {
      close();
    } else {
      setStickyOpen(true);
      setHoverOpen(true); // stay open even after mouse leaves
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  };

  // If focus leaves the whole dropdown (tabbing past it), close.
  // relatedTarget === null in Safari when losing focus to a non-focusable
  // target; treat as "focus gone" and close.
  const onContainerBlur = (e) => {
    const next = e.relatedTarget;
    if (!next || (containerRef.current && !containerRef.current.contains(next))) {
      close();
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setHoverOpen(true)}
      onMouseLeave={() => setHoverOpen(false)}
      onKeyDown={onKeyDown}
      onBlur={onContainerBlur}
      data-testid="topnav-libraries-dropdown"
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={onTriggerClick}
        onFocus={() => setHoverOpen(true)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={[
          "px-3.5 py-1.5 rounded-md transition-colors inline-flex items-center gap-1",
          active ? "bg-accent-soft text-accent font-medium" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
        ].join(" ")}
        style={{ fontSize: 13 }}
        data-testid="topnav-libraries-trigger"
      >
        Libraries <ChevronDown size={13} />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full pt-1 w-56 z-50"
          data-testid="topnav-libraries-menu-wrap"
        >
          <div
            className="bg-surface border border-line rounded-md shadow-card overflow-hidden"
            role="menu"
            data-testid="topnav-libraries-menu"
          >
          {LIBRARY_ITEMS.map((it) => {
            const isActive = it.match.test(loc.pathname);
            return (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                onMouseDown={() => { close(); nav(it.href); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    close();
                    nav(it.href);
                    // After nav, focus is lost. Return focus to trigger
                    // so subsequent Tab presses continue naturally.
                    setTimeout(() => triggerRef.current?.focus(), 0);
                  }
                }}
                className={[
                  "w-full text-left px-3 py-2 transition-colors",
                  isActive ? "bg-accent-soft text-accent" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                ].join(" ")}
                style={{ fontSize: 12.5 }}
                data-testid={`topnav-libraries-item-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {it.label}
              </button>
            );
          })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SearchTrigger() {
  return (
    <button
      type="button"
      className="flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-md border border-line bg-surface hover:bg-surface-2 transition-colors text-ink-3"
      style={{ minWidth: 240, fontSize: 12.5 }}
      title="Global search — LT-21 hero"
    >
      <Search size={14} className="text-ink-3" />
      <span className="flex-1 text-left">Search everything</span>
      <kbd
        className="px-1.5 py-0.5 rounded border border-line bg-surface-2 text-ink-3 font-mono"
        style={{ fontSize: 10 }}
      >⌘K</kbd>
    </button>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex items-center gap-2 h-8 pl-1 pr-2 rounded-md hover:bg-surface-2 transition-colors"
      >
        <span
          className="w-7 h-7 rounded-full bg-accent text-white flex items-center justify-center font-medium"
          style={{ fontSize: 11 }}
        >
          {initials || "?"}
        </span>
        <ChevronDown size={13} className="text-ink-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-56 bg-surface border border-line rounded-md shadow-card overflow-hidden z-50">
          <div className="px-3 py-3 border-b border-line">
            <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
              {user?.name || "User"}
            </div>
            <div className="text-ink-3" style={{ fontSize: 11.5 }}>
              {user?.email}
            </div>
          </div>
          <button
            onMouseDown={() => navigate("/settings")}
            className="w-full flex items-center gap-2 px-3 py-2 text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12.5 }}
          >
            <UserIcon size={13} /> Account & preferences
          </button>
          <button
            onMouseDown={() => alert("Operations App link — wired in a later phase")}
            className="w-full flex items-center gap-2 px-3 py-2 text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12.5 }}
          >
            <ArrowLeftRight size={13} /> Switch to Operations App
          </button>
          <button
            onMouseDown={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-note-red hover:bg-surface-2 border-t border-line"
            style={{ fontSize: 12.5 }}
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function TopNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const brand = user?.preferred_brand === "lux_developments" ? "Lux Developments" : "Lux Construction";

  return (
    <header
      className="sticky top-0 z-50 bg-surface border-b border-line"
      style={{ height: 56 }}
    >
      <div className="app-container h-full flex items-center gap-6">
        <button
          type="button" onClick={() => nav("/dashboard")}
          className="flex items-center pr-2" style={{ minWidth: 220 }}
        >
          <BrandMark brand={brand} />
        </button>
        <nav className="flex items-center gap-1 flex-1">
          {NAV_ITEMS.map((it, idx) => {
            const active = it.match ? it.match.test(loc.pathname) : loc.pathname === it.href;
            return (
              <React.Fragment key={it.label}>
                <NavLink
                  {...it}
                  active={active}
                  onClick={() => nav(it.href)}
                />
                {/* Insert the Libraries dropdown between Tenders and Settings */}
                {it.label === "Tenders" ? <LibrariesDropdown /> : null}
              </React.Fragment>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <SearchTrigger />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
